import React, { useRef, useEffect, useState } from 'react';
import { motion, useScroll, useSpring, useInView } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

// --- NEW --- Placeholder for team member photos
const placeholderPFP = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23d1d5db'%3E%3Cpath fill-rule='evenodd' d='M18.685 19.097A9.723 9.723 0 0021.75 12c0-5.385-4.365-9.75-9.75-9.75S2.25 6.615 2.25 12a9.723 9.723 0 003.065 7.097A9.716 9.716 0 0012 21.75a9.716 9.716 0 006.685-2.653zm-12.54-1.285A7.486 7.486 0 0112 15a7.486 7.486 0 016.855 2.812A8.224 8.224 0 0112 20.25a8.224 8.224 0 01-5.855-2.438zM15.75 9a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z' clip-rule='evenodd'/%3E%3C/svg%3E`;

// --- HELPER ICONS (Unchanged) ---
const ClockIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);
const LocationIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
);

// --- NEW --- SVG icons for different office types
const HeadquartersIcon = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M8 9l4 -4l4 4" /><path d="M12 5v3" /><path d="M3 21h18" /><path d="M5 21v-10l7 -4l7 4v10" /><path d="M9 21v-5a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v5" />
    </svg>
);
const BranchIcon = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 21v-14a3 3 0 0 1 3 -3h10a3 3 0 0 1 3 3v14" /><path d="M9 21v-8a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v8" /><path d="M12 13h-2" /><path d="M12 4a2 2 0 1 1 4 0v2h-4v-2z" />
    </svg>
);
const IndustrialOfficeIcon = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M3 21h18" /><path d="M5 21v-12l5 4v-4l5 4h4" /><path d="M19 21v-8l-5.5 -4l-5.5 4v8" /><path d="M9 17v4" /><path d="M15 17v4" /><path d="M12 17v4" />
    </svg>
);


// --- MODIFIED --- Expanded data structure with new icons, gmapsUrl, and empty team photo
const officesData = [
    {
        id: 'o1', name: 'Creativad Headquarters', nameAr: 'مقر كرياتيفاد الرئيسي',
        icon: <HeadquartersIcon className="w-9 h-9 sm:w-11 sm:h-11" />, // New SVG Icon
        location: 'Hazmieh, Beirut', locationAr: 'الحازمية، بيروت',
        gmapsUrl: 'https://maps.app.goo.gl/aCUGktXXeEV6NAAr7?g_st=ic', // New GMaps URL
        hours: 'Mon - Fri, 9 AM - 4 PM', hoursAr: 'الإثنين - الجمعة، 9 صباحاً - 4 مساءً',
        team: [
            { id: 't1', name: 'Hoteit Hoteit', nameAr: 'حطيط حطيط', title: 'Founder & Owner', titleAr: 'مؤسس ومالك', photo: '' }, // Empty photo
            { id: 't2', name: 'Heba Kanso', nameAr: 'هبة قانصو', title: 'Community Manager', titleAr: 'مدير مجتمع', photo: '' }, // Empty photo
            { id: 't3', name: 'Asrar Hamieh', nameAr: 'أسرار حمية', title: 'Creative director', titleAr: 'مدير إبداعي', photo: '' }, // Empty photo
            { id: 't4', name: 'Farah Kamar', nameAr: 'فرح قمر', title: 'Marketing Manager', titleAr: 'مدير تسويق', photo: '' }, // Empty photo
            { id: 't5', name: 'Diana Farran', nameAr: 'ديانا فرّان', title: 'Senior Designer', titleAr: 'مدير مجتمع', photo: '' }, // Empty photo
            { id: 't6', name: 'Sahar Yehya', nameAr: 'سحر يحيى', title: 'Senior designer', titleAr: 'مدير مجتمع', photo: '' }, // Empty photo
            { id: 't7', name: 'Nadin Hadid', nameAr: 'نادين حديد', title: 'Senior designer', titleAr: 'مدير مجتمع', photo: '' }, // Empty photo
            { id: 't8', name: 'Zaynab Farran', nameAr: 'زينب فرّان', title: 'Web Developer', titleAr: 'مطورة مواقع', photo: '' }, // Empty photo
            { id: 't9', name: 'Sara Jaber', nameAr: 'سارة جابر', title: 'Media Production', titleAr: ' إنتاج إعلامي', photo: '' }, // Empty photo
        ]
    },
    {
        id: 'o2', name: 'C group USA', nameAr: 'فرع جدة',
        icon: <BranchIcon className="w-9 h-9 sm:w-11 sm:h-11" />, // New SVG Icon
        location: 'Williamson st Dearborn MI', locationAr: 'ويليامسون ش ديربورن MI',
        gmapsUrl: '#', // New GMaps URL
        hours: 'Mon - Fri, 9 AM - 4 PM', hoursAr: 'الإثنين - الجمعة، 9 صباحاً - 4 مساءً',
        team: [
            { id: 't10', name: 'Hoteit Hoteit', nameAr: 'حطيط حطيط', title: 'Co-founder ', titleAr: 'مؤسس مشارك', photo: '' }, // Empty photo
            { id: 't11', name: 'Ali Salame', nameAr: 'علي سلامة', title: 'Co-founder ', titleAr: 'مؤسس مشارك', photo: '' }, // Empty photo
            { id: 't12', name: 'Eddy Kattan', nameAr: 'إيدي قطان', title: 'Co-founder ', titleAr: 'مؤسس مشارك', photo: '' }, // Empty photo
        ]
    },
    {
        id: 'o3', name: 'Ted', nameAr: 'تيد',
        icon: <IndustrialOfficeIcon className="w-9 h-9 sm:w-11 sm:h-11" />, // New SVG Icon
        location: 'Doha, Qatar', locationAr: 'الدوحة، قطر',
        gmapsUrl: '#', // New GMaps URL
        hours: 'Sun - Thu, 9 AM - 4 PM', hoursAr: 'الأحد - الخميس، 9 صباحاً - 4 مساءً',
        team: [
            // { id: 't10', name: 'Fifth Member', nameAr: 'العضو الخامس', title: 'Logistics Lead', titleAr: 'مسؤول لوجستيات', photo: '' }, // Empty photo
        ]
    },
];

const PATH_WIDTH = 40;
const AMPLITUDE = 20;

// --- MODIFIED --- OfficeCard with clickable location and styled hours
const OfficeCard = ({ office, language }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: false, amount: 0.3 });
    const cardVariants = {
        hidden: { opacity: 0, scale: 0.95, rotateY: -60, filter: 'blur(8px)' },
        visible: { opacity: 1, scale: 1, rotateY: 0, filter: 'blur(0px)', transition: { type: 'spring', stiffness: 120, damping: 20 } },
    };
    return (
        <motion.div ref={ref} variants={cardVariants} initial="hidden" animate={isInView ? "visible" : "hidden"} className="bg-white p-4 sm:p-5 rounded-xl shadow-lg border border-gray-100" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            <div className="flex flex-col items-center gap-3 text-center sm:flex-row sm:items-center sm:text-start sm:gap-4">
                {/* MODIFIED: Renders the SVG component icon */}
                <div className="text-teal-600">{office.icon}</div>
                <div className="flex-1">
                    <h2 className="text-lg sm:text-xl font-greycliff font-bold text-gray-800">{language === 'ar' ? office.nameAr : office.name}</h2>
                    <div className="mt-2 space-y-2 text-gray-600 text-xs sm:text-sm">
                        <div className="flex items-center justify-center sm:justify-start gap-2.5">
                            <LocationIcon />
                            {/* MODIFIED: Location is now a styled link */}
                            <a href={office.gmapsUrl} target="_blank" rel="noopener noreferrer" className="font-greycliff font-bold text-[#58B2A3] no-underline hover:text-teal-600 transition-colors">
                                {language === 'ar' ? office.locationAr : office.location}
                            </a>
                        </div>
                        <div className="flex items-center justify-center sm:justify-start gap-2.5">
                            <ClockIcon />
                            {/* MODIFIED: Hours text is now a custom color */}
                            <span className="text-[#348871]">{language === 'ar' ? office.hoursAr : office.hours}</span>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

// --- MODIFIED --- TeamMemberCard with fallback to placeholder PFP
const TeamMemberCard = ({ member, language }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: false, amount: 0.3 });
    const cardVariants = {
        hidden: { opacity: 0, scale: 0.95, rotateY: 60, filter: 'blur(8px)' },
        visible: { opacity: 1, scale: 1, rotateY: 0, filter: 'blur(0px)', transition: { type: 'spring', stiffness: 120, damping: 20 } },
    };
    return (
        <motion.div ref={ref} variants={cardVariants} initial="hidden" animate={isInView ? "visible" : "hidden"} className="flex items-center p-2.5 bg-white/80 backdrop-blur-sm rounded-lg shadow-md border border-gray-100" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            {/* MODIFIED: Image src now falls back to placeholderPFP if member.photo is empty */}
            <img
                src={member.photo || placeholderPFP}
                alt={member.name}
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover shrink-0 border-2 border-white bg-gray-200"
            />
            <div className={`flex-grow ${language === 'ar' ? "me-3" : "ms-3"}`}>
                <h3 className="font-greycliff font-normal text-sm sm:text-base text-gray-800">{language === 'ar' ? member.nameAr : member.name}</h3>
                <p className="text-gray-500 text-xs">{language === 'ar' ? member.titleAr : member.title}</p>
            </div>
        </motion.div>
    );
};

// --- CORE COMPONENT (Structurally unchanged) ---
export default function MeetTheTeam() {
    const { language } = useLanguage();
    const containerRef = useRef(null);
    const [height, setHeight] = useState(1000);

    useEffect(() => {
        const element = containerRef.current;
        if (!element) return;
        const observer = new ResizeObserver(() => setHeight(element.scrollHeight));
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    const { scrollYProgress } = useScroll({ target: containerRef, offset: ['start center', 'end center'], });
    const pathProgress = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

    const pathD = (() => {
        if (height <= 0) return "";
        const segments = Math.max(10, officesData.length * 4);
        const segmentHeight = height / segments;
        let d = `M ${PATH_WIDTH / 2}, 0`;
        for (let i = 0; i <= segments; i++) {
            const y1 = segmentHeight * (i + 1);
            const cpx = PATH_WIDTH / 2 + (i % 2 === 0 ? AMPLITUDE : -AMPLITUDE);
            d += ` C ${cpx},${y1 - segmentHeight * 0.66} ${cpx},${y1 - segmentHeight * 0.33} ${PATH_WIDTH / 2},${y1}`;
        }
        return d;
    })();

    const mainContentPadding = 'md:pl-48';

    return (
        <div id="offices" className={`bg-gray-50/50 py-16 md:py-24 overflow-hidden ${mainContentPadding}`} style={{ perspective: '1000px' }}>
            <div className="container mx-auto px-4">
                <motion.div initial={{ opacity: 0, y: -30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="text-center mb-12 md:mb-16" dir={language === 'ar' ? 'rtl' : 'ltr'}>
                    <h1 className="text-4xl md:text-5xl font-greycliff font-greycliff font-bold text-neutral-900">{language === 'ar' ? 'تعرف على فريقنا' : 'Meet Our Team'}</h1>
                    <p className="mt-3 text-lg text-gray-600 max-w-2xl mx-auto">{language === 'ar' ? 'مجموعة من الخبراء المتفانين في مكاتبنا حول المملكة.' : 'A dedicated group of experts across our offices.'}</p>
                </motion.div>

                <div ref={containerRef} className="relative max-w-5xl mx-auto">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 h-full flex justify-center">
                        <svg width={PATH_WIDTH} height={height} viewBox={`0 0 ${PATH_WIDTH} ${height}`} fill="none" preserveAspectRatio="none">
                            <path d={pathD} stroke="#CBD5E1" strokeOpacity={0.8} strokeWidth={2} strokeDasharray="8, 6" />
                            <motion.path d={pathD} stroke="url(#grad-main)" strokeWidth={3} style={{ pathLength: pathProgress }} />
                            <defs> <linearGradient id="grad-main" x1="0" y1="0" x2="0" y2={height}> <stop offset="0%" stopColor="#2dd4bf" /> <stop offset="100%" stopColor="#0d9488" /> </linearGradient> </defs>
                        </svg>
                    </div>

                    <div className="relative z-10">
                        {officesData.map(office => (
                            <div key={office.id} className="mb-12 md:mb-16">
                                <div className="flex items-start">
                                    <div className="w-1/2 pr-4 md:pr-8 flex justify-end">
                                        <div className="w-full max-w-sm">
                                            <OfficeCard office={office} language={language} />
                                        </div>
                                    </div>
                                    <div className="w-1/2 pl-4 md:pl-8"></div>
                                </div>

                                <div className="flex items-start mt-6">
                                    <div className="w-1/2 pr-4 md:pr-8"></div>
                                    <div className="w-1/2 pl-4 md:pl-8">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            {office.team.map(member => (
                                                <TeamMemberCard key={member.id} member={member} language={language} />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}